/**
 * Reusable number validation utilities.
 * Designed for inputs that require positive decimals with a fixed number of decimal places,
 * with optional constraints on the integer part.
 */

export interface PositiveDecimalOptions {
  /** Maximum digits allowed before the decimal point (default: unlimited). */
  maxIntegerDigits?: number;
  /**
   * When true, the integer part must be exactly "0" — only values like 0.888 are accepted.
   * Takes precedence over maxIntegerDigits.
   */
  zeroIntegerOnly?: boolean;
}

/**
 * Returns a regex that matches a positive decimal during live input:
 * - `zeroIntegerOnly`: integer part must be exactly "0"  → e.g. "0.888" ✓  "1.234" ✗
 * - `maxIntegerDigits`: at most N digits before decimal   → e.g. maxIntegerDigits=1: "9.123" ✓  "88.1" ✗
 * - default: any number of digits before decimal
 *
 * Always allows up to `decimalPlaces` digits after the decimal point.
 */
export function positiveDecimalRegex(
  decimalPlaces: number,
  options: PositiveDecimalOptions = {},
): RegExp {
  const { zeroIntegerOnly, maxIntegerDigits } = options;
  let intPart: string;
  if (zeroIntegerOnly) {
    intPart = '0';
  } else if (maxIntegerDigits != null) {
    intPart = `[0-9]{1,${maxIntegerDigits}}`;
  } else {
    intPart = '\\d*';
  }
  return new RegExp(`^${intPart}(\\.\\d{0,${decimalPlaces}})?$`);
}

/**
 * Returns true if the string is a valid in-progress positive decimal input
 * (safe to accept while the user is typing — allows empty string and trailing dot).
 */
export function isValidPositiveDecimalInput(
  value: string,
  decimalPlaces = 3,
  options: PositiveDecimalOptions = {},
): boolean {
  if (value === '') return true;
  return positiveDecimalRegex(decimalPlaces, options).test(value);
}

/**
 * Returns true if the value is a complete, valid positive decimal number
 * satisfying the given options.
 */
export function isValidPositiveDecimal(
  value: number | string,
  decimalPlaces = 3,
  options: PositiveDecimalOptions = {},
): boolean {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num < 0) return false;

  const { zeroIntegerOnly, maxIntegerDigits } = options;
  const [intPart, fracPart] = String(num).split('.');
  const absInt = intPart.replace('-', '');

  if (zeroIntegerOnly && absInt !== '0') return false;
  if (!zeroIntegerOnly && maxIntegerDigits != null && absInt.length > maxIntegerDigits) return false;
  if (fracPart && fracPart.length > decimalPlaces) return false;
  return true;
}

/**
 * Parses a string as a positive decimal, returning 0 for invalid or negative input.
 */
export function parsePositiveDecimal(value: string): number {
  const parsed = parseFloat(value);
  return isNaN(parsed) || parsed < 0 ? 0 : parsed;
}

/**
 * Formats a number to a fixed number of decimal places.
 */
export function formatDecimalPlaces(value: number, decimalPlaces = 3): string {
  return value.toFixed(decimalPlaces);
}

/**
 * Creates an Ant Design Form `validator` rule for a positive decimal field.
 * Use inside a Form.Item `rules` array:
 *   rules={[{ validator: createPositiveDecimalValidator(3, 'Thickness', { zeroIntegerOnly: true }) }]}
 */
export function createPositiveDecimalValidator(
  decimalPlaces = 3,
  fieldName = 'Value',
  options: PositiveDecimalOptions = {},
): (_: unknown, value: string | number | undefined) => Promise<void> {
  return (_, value) => {
    if (value === '' || value === undefined || value === null) return Promise.resolve();
    if (!isValidPositiveDecimal(value, decimalPlaces, options)) {
      const constraint = options.zeroIntegerOnly
        ? ' in the format 0.xxx (must start with 0)'
        : options.maxIntegerDigits != null
          ? ` with at most ${options.maxIntegerDigits} digit(s) before the decimal point`
          : '';
      return Promise.reject(
        new Error(
          `${fieldName} must be a positive number with up to ${decimalPlaces} decimal places${constraint}`,
        ),
      );
    }
    return Promise.resolve();
  };
}
